import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Sparkles, Wand2, Upload, X, Copy, Save, RefreshCw, ShieldCheck, ArrowLeft,
  ImagePlus, Facebook, Instagram, Globe, ChevronRight, Loader2, Heart, Share2, MessageCircle,
  Pencil, Check, Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import Navbar from '../components/Navbar';
import { businessTypes, tones, platforms, heroImages } from '../mock';
import api from '../lib/api';
import { toast } from '../hooks/use-toast';
import FeedbackWidget from '../components/FeedbackWidget';

const Create = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);

  const [businessName, setBusinessName] = useState('');
  const [businessType, setBusinessType] = useState('takeaway');
  const [tone, setTone] = useState('friendly');
  const [platform, setPlatform] = useState('both');
  const [brief, setBrief] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [aiImage, setAiImage] = useState(true);

  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editingAdId, setEditingAdId] = useState(null);  // when re-opened from Dashboard

  // Helper: keep result fields editable by updating individual keys
  const updateResult = (patch) => setResult(r => r ? { ...r, ...patch } : r);

  // Load an existing saved ad if /create?adId=... was used (Dashboard "Open in Create")
  useEffect(() => {
    const adId = searchParams.get('adId');
    if (!adId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/ads');
        const ad = (data.ads || []).find(a => a.id === adId);
        if (cancelled || !ad) return;
        setBusinessName(ad.businessName || '');
        setBusinessType(ad.businessType || 'takeaway');
        setTone(ad.tone || 'friendly');
        setPlatform(ad.platform || 'both');
        setBrief(ad.brief || '');
        setResult({
          headline: ad.headline,
          body: ad.body,
          cta: ad.cta,
          hashtags: ad.hashtags,
          image: ad.image || '',
          complianceCheck: ad.complianceCheck,
          businessName: ad.businessName,
          businessType: ad.businessType,
          tone: ad.tone,
          platform: ad.platform,
          brief: ad.brief,
        });
        setEditingAdId(ad.id);
        setEditing(true);
        toast({ title: 'Ad loaded', description: 'Edit anything below or click Generate to regenerate.' });
      } catch {
        toast({ title: 'Could not load ad', description: 'Please try again from the dashboard.' });
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams]);

  const sampleBriefs = [
    'New artisan bakery in Leeds, sourdough special this Saturday £6 with a coffee.',
    'Mobile dog grooming service covering Manchester, free nail trim with first booking.',
    'Family-run Indian takeaway, 20% off all orders over £25 this weekend.',
    'Beauty salon offering bridal packages, August booking discount 15%.',
  ];

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setUploadedImage(reader.result);
      setAiImage(false);
    };
    reader.readAsDataURL(file);
  };

  const handleGenerate = async () => {
    if (!brief.trim()) {
      toast({ title: 'Tell us about your business', description: 'Add a brief description to generate.' });
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      // 1. Generate text
      const { data: ad } = await api.post('/ads/generate', {
        businessName, businessType, tone, platform, brief,
      });

      let imageData = uploadedImage;
      // 2. Generate image if user picked AI
      if (aiImage && !uploadedImage) {
        try {
          const { data: img } = await api.post('/ads/generate-image', {
            brief, businessType, businessName,
          });
          imageData = img.image;
        } catch (e) {
          // Fallback to a stock image if image gen fails
          const fallback = [heroImages.cafe, heroImages.florist, heroImages.apron, heroImages.grocery];
          imageData = fallback[Math.floor(Math.random() * fallback.length)];
          toast({ title: 'Used stock image', description: 'AI image service was busy \u2014 used a stock photo instead.' });
        }
      }

      setResult({
        ...ad,
        image: imageData,
        businessName: businessName || 'Your Business',
        businessType,
        tone,
        platform,
        brief,
      });
    } catch (err) {
      toast({
        title: 'Generation failed',
        description: err?.response?.data?.detail || 'Please try again in a moment.',
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!result) return;
    try {
      if (editingAdId) {
        await api.put(`/ads/${editingAdId}`, result);
        toast({ title: 'Changes saved', description: 'Your saved ad has been updated.' });
      } else {
        await api.post('/ads', result);
        toast({ title: 'Ad saved', description: 'You can find it in your dashboard.' });
      }
    } catch (err) {
      toast({ title: 'Could not save', description: err?.response?.data?.detail || 'Please try again.' });
    }
  };

  const buildShareText = () => {
    if (!result) return '';
    return `${result.headline}\n\n${result.body}\n\n${result.hashtags}`;
  };

  const dataUrlToFile = async (dataUrl, filename) => {
    const res = await fetch(dataUrl);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type || 'image/png' });
  };

  const handleCopy = async () => {
    if (!result) return;
    const text = buildShareText();
    await navigator.clipboard.writeText(text);
    toast({ title: 'Text copied', description: 'Paste wherever you like.' });
  };

  const handleDownloadImage = async () => {
    if (!result?.image) return;
    try {
      const file = await dataUrlToFile(result.image, `mbap-ad-${Date.now()}.png`);
      const url = URL.createObjectURL(file);
      const a = document.createElement('a');
      a.href = url; a.download = file.name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({ title: 'Image downloaded', description: 'Attach it when you paste the caption.' });
    } catch {
      toast({ title: 'Download failed', description: 'Long-press the preview image to save instead.' });
    }
  };

  const handleShareAll = async () => {
    if (!result) return;
    const text = buildShareText();
    try {
      // Best path: native share with image + caption (mobile + some desktop browsers)
      if (result.image && navigator.canShare) {
        const file = await dataUrlToFile(result.image, 'ad-image.png');
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: result.headline || 'My ad',
            text,
          });
          return;
        }
      }
      // Fallback for plain share API (text-only)
      if (navigator.share) {
        await navigator.share({ title: result.headline || 'My ad', text });
        return;
      }
      // Desktop fallback: copy text + auto-download image, so the user only needs to paste + attach
      await navigator.clipboard.writeText(text);
      if (result.image) await handleDownloadImage();
      toast({
        title: 'Ad ready to post',
        description: 'Caption copied to clipboard. Image downloaded — attach it when you paste into Facebook/Instagram.',
      });
    } catch (e) {
      if (e?.name === 'AbortError') return; // user cancelled native share
      toast({ title: 'Share not supported', description: 'Use Copy text + Download image instead.' });
    }
  };

  const PlatformIcon = platform === 'facebook' ? Facebook : platform === 'instagram' ? Instagram : Globe;

  return (
    <div className="min-h-screen bg-[#fdfaf6]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </button>

        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">Create a new ad</h1>
            <p className="mt-1 text-slate-600">Describe your business or offer. AI handles the rest.</p>
          </div>
          <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 px-3 py-1.5">
            <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> UK ASA Compliance enabled
          </Badge>
        </div>

        <div className="mt-8 grid lg:grid-cols-5 gap-6">
          {/* FORM */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-6 h-fit">
            <div className="space-y-5">
              <div className="space-y-2">
                <Label>Business name</Label>
                <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="e.g. Bloom & Petal Florists" className="h-11" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Business type</Label>
                  <Select value={businessType} onValueChange={setBusinessType}>
                    <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {businessTypes.map(b => <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tone of voice</Label>
                  <Select value={tone} onValueChange={setTone}>
                    <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {tones.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Platform</Label>
                <div className="grid grid-cols-3 gap-2">
                  {platforms.map(p => {
                    const Icon = p.icon === 'Facebook' ? Facebook : p.icon === 'Instagram' ? Instagram : Globe;
                    return (
                      <button
                        key={p.id}
                        onClick={() => setPlatform(p.id)}
                        className={`h-11 rounded-lg border text-sm font-medium flex items-center justify-center gap-1.5 transition-all ${
                          platform === p.id
                            ? 'border-orange-500 bg-orange-50 text-orange-700'
                            : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                      >
                        <Icon className="w-4 h-4" /> {p.label.split(' ')[0]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>What do you want to advertise?</Label>
                <Textarea
                  value={brief}
                  onChange={(e) => setBrief(e.target.value)}
                  placeholder="Tell us in plain English. What's the offer? Who's it for? Anything special?"
                  className="min-h-[120px] resize-none"
                />
                <div className="flex flex-wrap gap-1.5 pt-1">
                  <span className="text-xs text-slate-400 mr-1">Try:</span>
                  {sampleBriefs.slice(0, 2).map((s, i) => (
                    <button
                      key={i}
                      onClick={() => setBrief(s)}
                      className="text-xs bg-slate-100 hover:bg-orange-100 text-slate-700 hover:text-orange-700 px-2.5 py-1 rounded-full transition-colors"
                    >
                      {s.slice(0, 35)}…
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Image</Label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => { setAiImage(true); setUploadedImage(null); }}
                    className={`h-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-all ${
                      aiImage ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Wand2 className={`w-5 h-5 ${aiImage ? 'text-orange-600' : 'text-slate-400'}`} />
                    <span className="text-xs font-medium">AI generate</span>
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`h-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-all overflow-hidden ${
                      uploadedImage ? 'border-orange-500' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    style={uploadedImage ? { backgroundImage: `url(${uploadedImage})`, backgroundSize: 'cover' } : {}}
                  >
                    {!uploadedImage && (
                      <>
                        <Upload className="w-5 h-5 text-slate-400" />
                        <span className="text-xs font-medium">Upload yours</span>
                      </>
                    )}
                  </button>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
              </div>

              <Button
                onClick={handleGenerate}
                disabled={generating}
                className="w-full h-12 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white font-semibold rounded-xl text-base"
              >
                {generating ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Crafting your ad…</>
                ) : (
                  <><Sparkles className="w-5 h-5 mr-2" /> Generate with AI</>
                )}
              </Button>
            </div>
          </div>

          {/* PREVIEW */}
          <div className="lg:col-span-3 space-y-4">
            {!result && !generating && (
              <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-orange-100 to-rose-100 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-orange-500" />
                </div>
                <h3 className="mt-4 text-xl font-bold text-slate-900">Your ad will appear here</h3>
                <p className="mt-1 text-slate-500 max-w-sm mx-auto">Fill in the brief on the left and we'll generate scroll-stopping copy & visuals.</p>
              </div>
            )}

            {generating && (
              <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center">
                <Loader2 className="w-10 h-10 mx-auto text-orange-500 animate-spin" />
                <h3 className="mt-4 text-xl font-bold text-slate-900">Writing your ad…</h3>
                <p className="mt-1 text-slate-500">Compliance-checking, hashtag-picking, image-styling.</p>
              </div>
            )}

            {result && (
              <>
                {/* Action bar */}
                <div className="flex flex-wrap gap-2">
                  <Button onClick={handleShareAll} data-testid="share-ad-btn" className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white">
                    <Share2 className="w-4 h-4 mr-1.5" /> Share ad (image + caption)
                  </Button>
                  <Button onClick={handleSave} data-testid="save-ad-btn" variant="outline" className="border-slate-300">
                    <Save className="w-4 h-4 mr-1.5" /> Save
                  </Button>
                  <Button variant="outline" onClick={handleCopy} data-testid="copy-text-btn">
                    <Copy className="w-4 h-4 mr-1.5" /> Copy text
                  </Button>
                  {result.image && (
                    <Button variant="outline" onClick={handleDownloadImage} data-testid="download-image-btn">
                      <Download className="w-4 h-4 mr-1.5" /> Download image
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleGenerate} data-testid="regenerate-btn">
                    <RefreshCw className="w-4 h-4 mr-1.5" /> Regenerate
                  </Button>
                  <Button variant="outline" onClick={() => navigate('/share-guide')} data-testid="how-to-post-btn">
                    <Globe className="w-4 h-4 mr-1.5" /> How to post
                  </Button>
                </div>

                {/* Compliance */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex gap-3">
                  <ShieldCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold text-emerald-900 text-sm">UK ASA Compliance: Passed</div>
                    <ul className="mt-1 space-y-0.5">
                      {result.complianceCheck.notes.map((n, i) => (
                        <li key={i} className="text-xs text-emerald-700">✓ {n}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Social Preview */}
                <SocialPreview ad={result} platform={platform} businessName={result.businessName} />

                {/* Raw text - EDITABLE */}
                <div className="bg-white border border-slate-200 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-bold text-slate-900">Generated content</h3>
                    <div className="flex items-center gap-2">
                      <PlatformIcon className="w-5 h-5 text-slate-400" />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditing(e => !e)}
                        data-testid="toggle-edit-btn"
                        className="h-8 px-3"
                      >
                        {editing ? (<><Check className="w-3.5 h-3.5 mr-1" /> Done editing</>) : (<><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</>)}
                      </Button>
                    </div>
                  </div>
                  {!editing && (
                    <p className="text-xs text-slate-500 -mt-1 mb-3">
                      Click <span className="font-semibold">Edit</span> to fix any wording before sharing — the live preview will update as you type.
                    </p>
                  )}
                  <div className="space-y-3 text-sm">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Headline</div>
                      {editing ? (
                        <Input
                          data-testid="edit-headline"
                          value={result.headline}
                          onChange={(e) => updateResult({ headline: e.target.value })}
                          className="font-semibold"
                        />
                      ) : (
                        <div className="text-slate-900 font-semibold">{result.headline}</div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Body</div>
                      {editing ? (
                        <Textarea
                          data-testid="edit-body"
                          value={result.body}
                          onChange={(e) => updateResult({ body: e.target.value })}
                          className="min-h-[140px]"
                        />
                      ) : (
                        <div className="text-slate-700 whitespace-pre-line">{result.body}</div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Call to action</div>
                      {editing ? (
                        <Input
                          data-testid="edit-cta"
                          value={result.cta}
                          onChange={(e) => updateResult({ cta: e.target.value })}
                        />
                      ) : (
                        <div className="text-slate-900">{result.cta}</div>
                      )}
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold mb-1">Hashtags</div>
                      {editing ? (
                        <Input
                          data-testid="edit-hashtags"
                          value={result.hashtags}
                          onChange={(e) => updateResult({ hashtags: e.target.value })}
                          className="text-orange-600 font-medium"
                        />
                      ) : (
                        <div className="text-orange-600 font-medium">{result.hashtags}</div>
                      )}
                    </div>
                  </div>
                </div>
                {result.id && (
                  <div className="mt-4 max-w-md mx-auto">
                    <FeedbackWidget mediaType="ad" mediaId={result.id} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Social Preview component (Instagram/Facebook style)
const SocialPreview = ({ ad, platform, businessName }) => {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden max-w-md mx-auto">
      <div className="flex items-center gap-3 p-3 border-b border-slate-100">
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-rose-500 flex items-center justify-center text-white font-bold text-sm">
          {businessName.charAt(0)}
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-900">{businessName}</div>
          <div className="text-xs text-slate-500">Sponsored · 🌍</div>
        </div>
      </div>
      <div className="aspect-square bg-slate-100">
        <img src={ad.image} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="p-3">
        <div className="flex items-center gap-4">
          <Heart className="w-6 h-6 text-slate-700" />
          <MessageCircle className="w-6 h-6 text-slate-700" />
          <Share2 className="w-6 h-6 text-slate-700" />
        </div>
        <div className="mt-2 text-sm">
          <span className="font-semibold text-slate-900">{businessName}</span>{' '}
          <span className="font-semibold text-slate-900">{ad.headline}</span>
        </div>
        <div className="mt-1 text-sm text-slate-700 whitespace-pre-line">{ad.body}</div>
        <div className="mt-2 text-sm text-blue-600 font-medium">{ad.hashtags}</div>
        <button className="mt-3 w-full h-10 rounded-lg bg-slate-900 text-white font-semibold text-sm">
          {ad.cta}
        </button>
      </div>
    </div>
  );
};

export default Create;
